<template>
  <div class="min-h-screen">
    <div class="max-w-7xl mx-auto p-4 sm:p-6 md:p-8">
      <header class="mb-8">
        <div class="flex items-center justify-center sm:justify-start">
          <h1 class="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-slate-100 text-center sm:text-left">
            Saldo em <span class="text-brand-600 dark:text-brand-500">Contas</span>
          </h1>
          <button @click="toggleVisibility"
            class="ml-3 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 p-2 rounded-full transition-colors"
            :title="showValues ? 'Ocultar valores' : 'Mostrar valores'">
            <span v-if="showValues" class="material-symbols-outlined text-xl sm:text-2xl">visibility</span>
            <span v-else class="material-symbols-outlined text-xl sm:text-2xl">visibility_off</span>
          </button>
        </div>
        <p class="text-sm text-slate-600 dark:text-slate-400 text-center sm:text-left mt-1">Gerencie os saldos das suas
          contas.</p>
      </header>

      <div class="mb-8 flex justify-start">
        <button @click="handleNovoSaldo" class="btn-primary flex items-center justify-center text-sm">
          <span class="material-symbols-outlined mr-2 text-lg">add_circle</span>
          Adicionar Conta
        </button>
      </div>

      <div v-if="sortedSaldoBancoList.length > 0" class="mb-8">
        <div
          class="bg-gradient-to-r from-brand-600 to-brand-700 text-white p-6 rounded-xl shadow-lg flex flex-col sm:flex-row justify-between items-center">
          <div class="mb-2 sm:mb-0 text-center sm:text-left"><span class="text-base sm:text-lg font-medium">Saldo Total Geral</span>
          </div>
          <div><span class="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">{{ displayCurrency(totalSaldoGeral) }}</span></div>
        </div>
      </div>

      <div v-if="sortedSaldoBancoList.length === 0" class="empty-state">
        <span
          class="material-symbols-outlined text-6xl text-brand-400 dark:text-brand-700">account_balance_wallet</span>
        <p class="mt-4 font-medium">Nenhum saldo bancário cadastrado.</p>
        <p class="text-sm">Adicione uma conta para começar.</p>
      </div>

      <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <div v-for="item in sortedSaldoBancoList" :key="item.id" class="card-bank">
          <div class="p-4 flex items-center rounded-t-2xl" :class="getBankTheme(item.banco).bg">
            <img :src="getBankLogo(item.banco)" :alt="`Logo ${item.banco}`"
              class="w-8 h-8 rounded-full object-contain bg-white shadow-sm p-0.5">
            <h3 class="font-bold text-lg ml-3" :class="getBankTheme(item.banco).text_contrast">{{ item.banco }}</h3>
          </div>
          <div class="p-5 flex-grow">
            <p class="text-sm text-slate-500 dark:text-slate-400">Saldo em conta</p>
            <p class="text-2xl sm:text-3xl font-bold text-slate-700 dark:text-slate-200 tracking-tight">{{
              displayCurrency(item.saldo) }}</p>
          </div>
          <div class="card-footer">
            <button @click="handleEditSaldo(item)" title="Editar Saldo"
              class="btn-action-icon text-sky-600 dark:text-sky-400 hover:bg-sky-100 dark:hover:bg-slate-600">
              <span class="material-symbols-outlined text-lg">edit</span>
            </button>
            <button @click="confirmDelete(item.id)" title="Excluir Saldo"
              class="btn-action-icon text-red-500 dark:text-red-400 hover:bg-red-100 dark:hover:bg-slate-600">
              <span class="material-symbols-outlined text-lg">delete</span>
            </button>
          </div>
        </div>
      </div>

      <transition name="modal-fade">
        <div v-if="showModal" @click.self="closeModal" class="modal-backdrop">
          <div class="modal-container">
            <header class="modal-header">
              <h3 class="text-xl font-semibold">{{ isEditing ? 'Editar Saldo' : 'Adicionar Saldo' }}</h3>
              <button @click="closeModal" title="Fechar" :disabled="isSubmitting" class="modal-close-btn"><span
                  class="material-symbols-outlined">close</span></button>
            </header>
            <form @submit.prevent="handleSubmit" class="p-6 space-y-4">
              <div>
                <label for="bancoSelectModal" class="form-label">Banco</label>
                <select id="bancoSelectModal" v-model="form.banco" required class="form-input">
                  <option disabled value="">Selecione um banco</option>
                  <option v-for="banco in bankOptions" :key="banco" :value="banco">{{ banco }}</option>
                </select>
              </div>
              <div v-if="form.banco === 'Outro'">
                <label for="bancoCustomModal" class="form-label">Nome do Banco</label>
                <input id="bancoCustomModal" v-model="form.customBanco" type="text" required
                  placeholder="Ex: Banco Neon" class="form-input" />
              </div>
              <div>
                <label for="bancoSaldoModal" class="form-label">Saldo (R$)</label>
                <input id="bancoSaldoModal" v-model.number="form.saldo" type="number" step="0.01" required
                  placeholder="0.00" class="form-input" />
              </div>
              <footer class="modal-footer !p-0 !pt-4 !bg-transparent">
                <button @click="closeModal" type="button" :disabled="isSubmitting"
                  class="btn-secondary">Cancelar</button>
                <button type="submit" :disabled="isSubmitting" class="btn-primary flex items-center">
                  <span v-if="isSubmitting"
                    class="material-symbols-outlined animate-spin mr-2 text-lg">progress_activity</span>
                  {{ isSubmitting ? 'Salvando...' : (isEditing ? 'Atualizar' : 'Adicionar') }}
                </button>
              </footer>
            </form>
          </div>
        </div>
      </transition>

      <transition name="modal-fade">
        <div v-if="showDeleteConfirm" @click.self="showDeleteConfirm = false" class="modal-backdrop">
          <div class="modal-container p-0 max-w-md">
            <div class="p-6 text-center">
              <span class="material-symbols-outlined text-5xl text-red-500 dark:text-red-400 mx-auto">warning</span>
              <h3 class="modal-title">Excluir Saldo</h3>
              <p class="modal-text">Tem certeza que deseja excluir este saldo?</p>
            </div>
            <div class="modal-footer">
              <button @click="showDeleteConfirm = false" type="button" class="btn-secondary">Cancelar</button>
              <button @click="executeDelete" class="btn-danger">Sim, Excluir</button>
            </div>
          </div>
        </div>
      </transition>

      <transition name="modal-fade">
        <div v-if="showUnsavedChangesConfirm" @click.self="showUnsavedChangesConfirm = false" class="modal-backdrop">
          <div class="modal-container p-0 max-w-md">
            <div class="p-6 text-center">
              <span class="material-symbols-outlined text-5xl text-amber-500 dark:text-amber-400 mx-auto">warning</span>
              <h3 class="modal-title">Descartar Alterações?</h3>
              <p class="modal-text">Você possui alterações não salvas. Deseja sair e perder os dados?</p>
            </div>
            <div class="modal-footer">
              <button @click="showUnsavedChangesConfirm = false" type="button" class="btn-secondary">Cancelar</button>
              <button @click="handleConfirmClose" class="btn-danger">Sim, Descartar</button>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'

const router = useRouter()
const showValues = ref(true)
const saldoBancoList = ref([])
const showModal = ref(false)
const isEditing = ref(false)
const isSubmitting = ref(false)
const showDeleteConfirm = ref(false)
const itemParaDeletar = ref(null)
const isMobile = ref(false)

const checkDevice = () => {
  isMobile.value = window.innerWidth < 640
}

const form = ref({ id: null, banco: '', customBanco: '', saldo: null })
const apiUrl = 'https://backend.controlease.com.br/saldos-banco'
const pristineForm = ref(null)
const showUnsavedChangesConfirm = ref(false)

const isFormDirty = computed(() => {
  if (!pristineForm.value) return false
  return JSON.stringify(form.value) !== JSON.stringify(pristineForm.value)
})

const bankThemes = {
  'Santander': { bg: 'bg-red-600', text_contrast: 'text-white' },
  'Nubank': { bg: 'bg-purple-600', text_contrast: 'text-white' },
  'Banco do Brasil': { bg: 'bg-yellow-400', text_contrast: 'text-slate-900' },
  'C6 Bank': { bg: 'bg-gray-800', text_contrast: 'text-white' },
  'Itaú': { bg: 'bg-orange-500', text_contrast: 'text-white' },
  'Bradesco': { bg: 'bg-red-700', text_contrast: 'text-white' },
  'Caixa': { bg: 'bg-blue-600', text_contrast: 'text-white' },
  'Inter': { bg: 'bg-orange-500', text_contrast: 'text-white' },
  'PagBank': { bg: 'bg-yellow-500', text_contrast: 'text-slate-900' },
  'PicPay': { bg: 'bg-green-500', text_contrast: 'text-white' },
  'Mercado Pago': { bg: 'bg-sky-500', text_contrast: 'text-white' },
  'Outro': { bg: 'bg-slate-500', text_contrast: 'text-white' }
}

const bankLogos = {
  'Santander': 'https://assets.hgbrasil.com/finance/companies/big/santander-br.png',
  'Nubank': 'https://logodownload.org/wp-content/uploads/2019/08/nubank-logo-2-1.png',
  'Banco do Brasil': 'https://logopng.com.br/logos/banco-do-brasil-5.png',
  'C6 Bank': 'https://www.jornalcontabil.com.br/wp-content/uploads/2019/11/c6-1200x1200.png',
  'Itaú': 'https://upload.wikimedia.org/wikipedia/commons/2/2d/2023_Ita%C3%BA_Unibanco_Logo.png',
  'Bradesco': 'https://logodownload.org/wp-content/uploads/2018/09/bradesco-logo-4.png',
  'Caixa': 'https://www.freepnglogos.com/uploads/logo-caixa-png/caixa-download-png-5.png',
  'Inter': 'https://yt3.googleusercontent.com/c_fQMiJzVdFb80Mr79X5pJzLw2VoFtVab3fLLcUqJzpe-cmNti04vLsQ7bDVGcpHa0UMlrjg-KU=s900-c-k-c0x00ffffff-no-rj',
  'PagBank': 'https://yt3.googleusercontent.com/dhhzAXfQDqT4ubXWVCcxRCT5WE6AvmZjARlYrMwmfel8WvLcqNSKdl4Ijya9m73UVIoIyZeV=s900-c-k-c0x00ffffff-no-rj',
  'PicPay': 'https://play-lh.googleusercontent.com/pTvc9kCumx_24eJDwGUpvcBwljcIBkrsL3qHwhBW2NalMQ-XxTtHRV9YAJanBxkV0Rw',
  'Mercado Pago': 'https://cdn.shopify.com/app-store/listing_images/4f3dc60a0ef8beee2a168713fe20181f/icon/CIHzwOjZsI0DEAE=.png',
  'Outro': 'https://w7.pngwing.com/pngs/52/580/png-transparent-bank-account-computer-icons-bank-text-payment-logo.png'
}

const bankOptions = computed(() => {
  return Object.keys(bankThemes).sort((a, b) => {
    if (a === 'Outro') return 1
    if (b === 'Outro') return -1
    return a.localeCompare(b)
  })
})

const getBankTheme = (banco) => {
  const foundBank = Object.keys(bankThemes).find(key => banco.toLowerCase().includes(key.toLowerCase()))
  return foundBank ? bankThemes[foundBank] : bankThemes['Outro']
}

const getBankLogo = (banco) => {
  const foundBank = Object.keys(bankLogos).find(key => banco.toLowerCase().includes(key.toLowerCase()))
  return foundBank ? bankLogos[foundBank] : bankLogos['Outro']
}

const toggleVisibility = () => {
  showValues.value = !showValues.value
  localStorage.setItem('showFinancialValues', JSON.stringify(showValues.value))
}

const formatCurrency = (value) => {
  if (typeof value !== 'number') return 'R$ 0,00'
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

const displayCurrency = (value) => (showValues.value ? formatCurrency(value) : 'R$ ●●●●●')

const authConfig = () => {
  const token = localStorage.getItem('token')
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {}
}

const fetchSaldoBanco = async () => {
  try {
    const response = await axios.get(apiUrl, authConfig())
    saldoBancoList.value = response.data
  } catch {
    //
  }
}

const handleNovoSaldo = () => {
  if (isMobile.value) {
    router.push({ name: 'SaldoBancoAdd' })
  } else {
    showAddSaldoBancoModal()
  }
}

const handleEditSaldo = (item) => {
  if (isMobile.value) {
    router.push({ name: 'SaldoBancoEdit', params: { id: item.id } })
  } else {
    editSaldoBanco(item)
  }
}

const showAddSaldoBancoModal = () => {
  resetForm()
  isEditing.value = false
  pristineForm.value = JSON.parse(JSON.stringify(form.value))
  showModal.value = true
}

const handleSubmit = async () => {
  if (isSubmitting.value) return
  isSubmitting.value = true

  const finalBankName = form.value.banco === 'Outro' ? form.value.customBanco : form.value.banco
  if (!finalBankName) {
    alert('O nome do banco é obrigatório.')
    isSubmitting.value = false
    return
  }

  const payload = { id: form.value.id, banco: finalBankName, saldo: form.value.saldo }

  try {
    if (isEditing.value) {
      await axios.put(`${apiUrl}/${payload.id}`, payload, authConfig())
    } else {
      await axios.post(apiUrl, payload, authConfig())
    }
    await fetchSaldoBanco()
    forceCloseModal()
  } catch {
    alert('Erro ao salvar saldo em banco.')
  } finally {
    isSubmitting.value = false
  }
}

const editSaldoBanco = (item) => {
  isEditing.value = true
  const isKnownBank = bankOptions.value.includes(item.banco)
  if (isKnownBank) {
    form.value = { id: item.id, banco: item.banco, customBanco: '', saldo: item.saldo }
  } else {
    form.value = { id: item.id, banco: 'Outro', customBanco: item.banco, saldo: item.saldo }
  }
  pristineForm.value = JSON.parse(JSON.stringify(form.value))
  showModal.value = true
}

const confirmDelete = (id) => {
  itemParaDeletar.value = id
  showDeleteConfirm.value = true
}

const executeDelete = async () => {
  if (itemParaDeletar.value === null) return
  try {
    await axios.delete(`${apiUrl}/${itemParaDeletar.value}`, authConfig())
    await fetchSaldoBanco()
  } catch {
    alert('Erro ao excluir saldo em banco.')
  } finally {
    showDeleteConfirm.value = false
    itemParaDeletar.value = null
  }
}

const closeModal = () => {
  if (isSubmitting.value) return
  if (isFormDirty.value) {
    showUnsavedChangesConfirm.value = true
  } else {
    forceCloseModal()
  }
}

const forceCloseModal = () => {
  showModal.value = false
  resetForm()
}

const handleConfirmClose = () => {
  showUnsavedChangesConfirm.value = false
  forceCloseModal()
}

const resetForm = () => {
  form.value = { id: null, banco: '', customBanco: '', saldo: null }
  isEditing.value = false
  pristineForm.value = null
}

const sortedSaldoBancoList = computed(() => {
  if (!saldoBancoList.value) return []
  return [...saldoBancoList.value].sort((a, b) => {
    const nameCompare = (a.banco || '').localeCompare(b.banco || '')
    if (nameCompare !== 0) return nameCompare
    return (a.id || 0) - (b.id || 0)
  })
})

const totalSaldoGeral = computed(() =>
  (saldoBancoList.value || []).reduce((total, item) => total + (parseFloat(item.saldo) || 0), 0)
)

onMounted(() => {
  const savedVisibility = localStorage.getItem('showFinancialValues')
  if (savedVisibility !== null) showValues.value = JSON.parse(savedVisibility)
  fetchSaldoBanco()
  checkDevice()
  window.addEventListener('resize', checkDevice)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkDevice)
})
</script>

<style scoped>
.form-input {
  @apply w-full px-3 py-2 text-sm bg-white dark:bg-slate-700/60 border border-solid border-slate-300 dark:border-slate-600 rounded-lg transition ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-opacity-70 focus:border-brand-500 text-slate-800 dark:text-slate-200;
}

.btn-action-icon {
  @apply p-1.5 rounded-full transition-colors duration-200;
}

.btn-primary {
  @apply px-5 py-2.5 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed;
}

.btn-secondary {
  @apply px-5 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 rounded-lg transition-colors duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed;
}

.btn-danger {
  @apply px-5 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors duration-150 ease-in-out;
}

.modal-backdrop {
  @apply fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 dark:bg-brand-900/80 backdrop-blur-sm p-4;
}

.modal-container {
  @apply bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md transform transition-all;
}

.modal-header {
  @apply px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center;
}

.modal-close-btn {
  @apply p-1 text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed;
}

.modal-footer {
  @apply px-4 py-3 bg-slate-50 dark:bg-slate-700/50 flex justify-end space-x-3 rounded-b-xl;
}

.modal-title {
  @apply mt-2 text-xl font-semibold text-slate-800 dark:text-slate-100;
}

.modal-text {
  @apply mt-2 text-sm text-slate-600 dark:text-slate-400;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(-20px);
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.card-bank {
  @apply bg-white dark:bg-brand-800 shadow-lg rounded-2xl flex flex-col overflow-hidden transition-transform hover:-translate-y-1 duration-300;
}

.card-footer {
  @apply mt-auto p-2 bg-slate-50 dark:bg-brand-800/50 flex items-center justify-end space-x-2 border-t border-slate-200 dark:border-brand-700;
}

.empty-state {
  @apply text-center py-16 text-slate-500 dark:text-slate-400 bg-white dark:bg-brand-800 rounded-xl shadow-md flex flex-col justify-center items-center;
}

.form-label {
  @apply block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1;
}
</style>
